/*
 *  -----------------------------------------------------------------------
 *    <copyright file="assign-modules/index.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */
import React, { useEffect, useState } from 'react';
import { useMutation } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DialogContent } from '@mui/material';

import { useBoolean, useTranslation } from 'hooks';
import { StudyFields } from 'queries/generated/fragments.generated';
import { GET_MANAGE_ORG_STUDIES, GET_STUDY, UPDATE_STUDY_MODULES } from 'queries/studies';
import { GET_ORGANISATION } from 'queries/uam';
import Constants from 'utils/constants';
import NotificationService from 'utils/notification';

import './styles.scss';
import Button from 'views/components/core/buttons';
import { CustomDialog, DialogActions, DialogTitle } from 'views/components/core/dialog';
import ItemList from 'views/components/core/item-list';

interface AssignModulesDialogInterface {
    open: boolean;
    close(action?: any): any;
    currentStudy: StudyFields;
    modules: string[];
    organisationId: string;
    studySettings?: boolean;
}

const AssignModulesDialog = ({
    open,
    close,
    currentStudy,
    modules,
    organisationId,
    studySettings,
}: AssignModulesDialogInterface) => {
    const { t } = useTranslation();
    const disabledSaveButton = useBoolean(true);
    const [modulesList, setModulesList] = useState([]);
    const [selectedItems, setSelectedItems] = useState([]);
    const [updateStudy] = useMutation(UPDATE_STUDY_MODULES);

    // (CM=1/001, RAMCore=2/010, ISMP=4/100).
    const setSelectedItemsByDefault = () => {
        const selectedByDefault: { name: string; id: number }[] = [];
        const currentModulesList = modules
            .filter((moduleKey) => moduleKey !== 'ISMP')
            .map((moduleKey: any) => {
                const moduleValue = (Constants.Modules as any)[moduleKey];
                if (currentStudy.modules & moduleValue) {
                    selectedByDefault.push({
                        name: t(`admin:${moduleKey}`),
                        id: moduleValue,
                    });
                }
                return {
                    name: t(`admin:${moduleKey}`),
                    id: moduleValue,
                };
            });
        setSelectedItems(selectedByDefault);
        return currentModulesList;
    };

    useEffect(() => {
        if (modules?.length && currentStudy) setModulesList(setSelectedItemsByDefault());
    }, [modules, currentStudy]);

    const onClose = (action?: string) => {
        if (modules?.length && currentStudy) setModulesList(setSelectedItemsByDefault());
        disabledSaveButton.setTrue();
        close(action);
    };

    const getRefetchQueriesSettings = () => {
        const queries = [
            {
                query: GET_ORGANISATION,
                variables: {
                    id: organisationId,
                },
            } as any,
        ];
        if (studySettings)
            queries.push({
                query: GET_STUDY,
                variables: {
                    id: currentStudy.id,
                },
            });
        else queries.push({ query: GET_MANAGE_ORG_STUDIES });
        return queries;
    };

    const submit = () => {
        const newModulesNumber = selectedItems.reduce((sum, module) => sum + module.id, 0);
        disabledSaveButton.setTrue();
        updateStudy({
            variables: {
                studyId: currentStudy.id,
                modules: newModulesNumber,
                reason: '',
            },
            refetchQueries: getRefetchQueriesSettings(),
        })
            .then(() => {
                NotificationService.showNotification(t('admin:moduleAssignedToStudy', { id: currentStudy.recordId }));
                disabledSaveButton.setTrue();
                close();
            })
            .catch(() => {
                NotificationService.showNotification(t('errorMessage'));
                disabledSaveButton.setFalse();
            });
    };

    return (
        <>
            {open && (
                <CustomDialog onClose={onClose} fullWidth maxWidth="md" scroll="paper" open={open}>
                    <DialogTitle id="assign-module-title">
                        {t('admin:assignModules')}
                        <FontAwesomeIcon
                            icon={['fal', 'times']}
                            className="fa-lg close-icon-cv"
                            onClick={() => {
                                onClose();
                            }}
                            data-testid="close-dialog"
                        />
                    </DialogTitle>
                    <DialogContent className="assign-module-dialog-content pb-0">
                        <>
                            <div className="list-info">
                                <p className="text-length">
                                    <span className="bold">{t('Study')}:</span> {` ${currentStudy?.recordId}`}
                                </p>
                                <p className="total-records">
                                    {t('admin:numberModuleRecords', { number: modulesList?.length })}
                                </p>
                            </div>
                            <ItemList
                                id="assign-module-list"
                                items={modulesList}
                                textField="name"
                                multiselect
                                closed={!open}
                                selectedItems={selectedItems}
                                onSelectionChange={(value: any) => {
                                    disabledSaveButton.setFalse();
                                    setSelectedItems(value);
                                }}
                            />
                        </>
                    </DialogContent>
                    <DialogActions className="assign-module-dialog-content-actions dialog-actions flex-end flex-wrap pt-0">
                        <div className="selected-items-container mb-20">
                            <p>
                                {selectedItems.length}/{modulesList?.length}
                            </p>
                        </div>
                        <Button
                            disabled={disabledSaveButton.value || !modulesList.length}
                            className="assign-module-btn"
                            text="update"
                            onClick={submit}
                        />
                    </DialogActions>
                </CustomDialog>
            )}
        </>
    );
};

export default AssignModulesDialog;
